import { GLOBAL_TRANSLATION_NAMESPACE, NO_DATA } from 'consts';
import { EnelXGuiAtoms, EnelXGuiComponents, EnelXGuiUtils } from 'gui';
import { t } from 'i18n';
import { getDefaultTimeOptionsMapper, isArrayEmpty } from 'utils';

const { searchbarLabelHandler } = EnelXGuiUtils;

const {
  MigrationSelect,
  MigrationToggleSwitch,
  SearchBar,
  AddressInput,
  MigrationSingleInput,
} = EnelXGuiAtoms;

const {
  MainSingleDate, MigrationSelectDateV3, MainAutoComplete,
} = EnelXGuiComponents;

const getSelectLabel = (outputs, id, defaultValue, options) => {
  if (isArrayEmpty(outputs) || isArrayEmpty(options)) return '';
  return (outputs?.find((e) => e.id === id)?.value || defaultValue) ?? '';
};

export const getDefaultActiveFilter = (values) => {
  const input = values?.find((e) => e?.type === 'input');
  if (!input) return null;
  return input?.options?.[0]?.id;
};

const getMultiSelectValues = ({ outputs, id, options }) => {
  const currentValues = outputs?.find((el) => el?.id === id)?.value;
  if (!currentValues) return [];
  return currentValues?.map((e) => options?.find((opt) => opt?.value === e));
};

const getSelectChipValue = (v, options) => ({ v: options.find(({ value }) => value === v)?.label || v });

export const typeMapper = ({
  epoch,
}) => {
  const commonProps = {
    placeholder: t(`${GLOBAL_TRANSLATION_NAMESPACE}:components.all`),
  };

  const {
    currentTimeZone: userTimeZone,
    countryFormatDate,
  } = epoch;

  return {
    singleDate: ({
      outputs, id, defaultValue, ...rest
    }) => ({
      Component: MainSingleDate,
      chipLabelFormatter: (v) => ({ v: searchbarLabelHandler(userTimeZone, v, countryFormatDate) }),
      componentProps: {
        ...commonProps,
        userTimeZone,
        value: outputs?.find((e) => e.id === id)?.value || defaultValue,
        ...rest,
      },
    }),
    date: ({
      outputs, id, defaultValue, ...rest
    }) => ({
      Component: MigrationSelectDateV3,
      chipLabelFormatter: (v) => {
        let values = v;
        if (!Array.isArray(v)) {
          values = getDefaultTimeOptionsMapper(userTimeZone)[v];
        }
        return { v: values?.map((val) => searchbarLabelHandler(userTimeZone, val, countryFormatDate)).join(' - ') };
      },
      componentProps: {
        ...commonProps,
        translation: t(`${GLOBAL_TRANSLATION_NAMESPACE}:components`),
        userTimeZone,
        value: outputs?.find((e) => e.id === id)?.value || defaultValue,
        customDropdownStyle: 'position: absolute;',
        ...rest,
      },
    }),
    singleInput: ({
      outputs, id, defaultValue, ...rest
    }) => ({
      Component: MigrationSingleInput,
      componentProps: {
        ...commonProps,
        externalValue: outputs?.find((e) => e.id === id)?.value || defaultValue,
        ...rest,
      },
    }),
    autocomplete: ({
      outputs, id, defaultValue, getResultList, ...rest
    }) => ({
      Component: MainAutoComplete,
      chipLabelFormatter: (v) => ({ v: v?.label }),
      componentProps: {
        ...commonProps,
        getResultList,
        onChangeKey: 'onSelect',
        externalValue: outputs?.find((e) => e.id === id)?.value || defaultValue,
        ...rest,
      },
    }),
    address: ({
      outputs, id, defaultValue, ...rest
    }) => ({
      Component: AddressInput,
      handleChange: (val) => val?.formatted_address,
      componentProps: {
        ...commonProps,
        onChangeKey: 'onChangeResult',
        value: outputs?.find((e) => e.id === id)?.value || defaultValue,
        ...rest,
      },
    }),
    select: ({
      outputs, id, defaultValue, options, onSelect, ...rest
    }) => ({
      Component: MigrationSelect,
      handleChange: (el) => {
        if (onSelect) onSelect(el?.value);
        return el?.value;
      },
      chipLabelFormatter: (v) => getSelectChipValue(v, options),
      componentProps: {
        ...commonProps,
        value: getSelectLabel(outputs, id, defaultValue, options),
        options,
        ...rest,
      },
    }),
    multiSelect: ({
      outputs, id, _defaultValue, options, selectAll, ...rest
    }) => ({
      Component: MigrationSelect,
      handleChange: (v) => v?.map((e) => e.value),
      chipLabelFormatter: (v) => getSelectChipValue(v, options),
      componentProps: {
        ...commonProps,
        multiple: true,
        selectAllLabel: selectAll ? t('components.selectAll') : null,
        options,
        values: getMultiSelectValues({ outputs, id, options }),
        onChangeKey: 'onMultipleChange',
        ...rest,
      },
    }),
    toggle: ({
      outputs, id, defaultValue, ...rest
    }) => ({
      Component: MigrationToggleSwitch,
      chipLabelFormatter: (v) => ({ v: v ? t('components.selected') : NO_DATA }),
      componentProps: {
        ...commonProps,
        checked: outputs?.find((e) => e.id === id)?.value || defaultValue,
        ...rest,
      },
    }),
    input: ({
      options, actualFilter, setActualFilter, outputs, visibleIds, ...rest
    }) => {
      const visibleOptions = visibleIds ? options.filter((opt) => visibleIds.indexOf(opt.id) !== -1) : options;
      const currentOption = visibleOptions.find((e) => e.id === actualFilter) ?? visibleOptions?.[0];

      return {
        Component: SearchBar,
        chipLabelFormatter: (_value, _label, id) => ({ l: options.find((e) => e.id === id)?.label }),
        componentProps: {
          ...commonProps,
          setFilter: (label) => setActualFilter(options.find((e) => e.label === label).id),
          actualFilter: currentOption?.label,
          options: visibleOptions?.map(({ label }) => label),
          arr: options, // NEEDED. THIS COMPONENT HANDLES ONLY LABELS, THIS IS AN EXTRA PROP TO MAKE SURE GUI SEARCHBAR HAS ACCESS TO IDS
          value: outputs?.find(({ id }) => id === currentOption?.id)?.value || '',
          ...rest,
        },
      };
    },
  };
};
